import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { Feature } from './Feature'
import { FKType } from './SQLBuilder'
import { SubaccountType } from './Subaccount'

type IndustryConstructorArgs = Omit<ConstructorArgs<() => Industry>, 'subaccount_type'> & {
    features: string | null
    subaccount_type: SubaccountType | undefined
}

export class Industry extends BaseRecord {
    readonly recordType = 'industry'

    duration?: number // Integer
    gratuity?: boolean
    estimate?: boolean
    recurring?: boolean
    classes?: boolean
    services?: boolean
    reservations?: boolean
    product?: boolean
    name?: string
    type?: string
    title?: string
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    private readonly _pluralizedTitle?: string
    readonly tier: number = 4
    readonly subaccountType: SubaccountType = 'none'
    readonly defaultFeatures: string[] = []

    constructor(data?: Partial<IndustryConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.name = data.name ?? undefined
        this.type = data.type ?? undefined
        this.title = data.title ?? undefined
        this.duration = this.propertyFromIntArg(data.duration)
        this.gratuity = data.gratuity ?? false
        this.estimate = data.estimate ?? false
        this.recurring = data.recurring ?? false
        this.classes = data.classes ?? false
        this.reservations = data.reservations ?? false
        this.services = data.services ?? false
        this.product = data.product ?? false
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this._pluralizedTitle = data.pluralized_title ?? undefined
        this.tier = (() => {
            if (data.tier === undefined || data.tier === null) {
                return 4
            }
            return data.tier === 1 ? 1 : data.tier === 2 ? 2 : data.tier === 3 ? 3 : 4
        })()
        this.subaccountType = (() => {
            if (data.subaccount_type === 'pet') return 'pet'
            if (data.subaccount_type === 'property') return 'property'
            if (data.subaccount_type === 'child') return 'child'
            if (data.subaccount_type === 'vehicle') return 'vehicle'
            if (data.subaccount_type === 'horse') return 'horse'
            return 'none'
        })()
        this.defaultFeatures = (data.features ?? '').split(',')
    }

    clone(overrides?: Partial<IndustryConstructorArgs>): Industry {
        return new Industry({
            ...this.serializeForClone(),
            subaccount_type: this.subaccountType,
            ...overrides,
        })
    }

    static initWithId(id: string): Industry {
        const industry = CacheFactory.getInstance().getRecord(Industry, id) ?? new Industry({ id })
        return industry
    }

    get pluralizedTitle(): string | undefined {
        return this._pluralizedTitle ?? `${this.title}s`
    }

    // todo: implement icon
    // get icon(): string {
    //     switch (this.id) {
    //         case "homeCleaning":
    //             return 'verticals_cleaner'
    //         case "lifeCoach":
    //             return 'verticals_coach'
    //         case "consulting":
    //             return 'verticals_consultant'
    //         case "contractor":
    //             return 'verticals_contractor'
    //         case "dogTraining":
    //             return 'verticals_dogtraining'
    //         case "esthetician":
    //             return 'verticals_esthetician'
    //         case "fitness":
    //             return 'verticals_fitness'
    //         case "hairStylist":
    //             return 'verticals_hair_stylist'
    //         case "healthWellness":
    //             return 'verticals_health_and_wellness'
    //         case "landscaping":
    //             return 'verticals_landscaper'
    //         case "makeupArtist":
    //             return 'verticals_makeup_artist'
    //         case "masseuse":
    //             return 'verticals_masseuse'
    //         case "mobileDetailing":
    //             return 'verticals_mobile_detailing'
    //         case "nailCare":
    //             return 'verticals_nail_care'
    //         case "petSitting":
    //             return 'verticals_pet_sitting'
    //         case "tattooArtist":
    //             return 'verticals_tatoo_artist'
    //         case "therapy":
    //             return 'verticals_therapy'
    //     }
    //     return 'verticals_other'
    // }

    defaultOn(feature: Feature): boolean {
        return this.defaultFeatures.indexOf(feature.id!) !== -1
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {}

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['name'] = this.name
        return dict
    }

    static dataType(): DataType {
        return 'industries'
    }
}
